import { z } from "zod";

// Reusable base validators
export const emailSchema = z
  .string()
  .min(1, "Email is required")
  .email("Invalid email");

export const passwordSchema = z
  .string()
  .min(8, "Minimum 8 characters")
  .regex(/[A-Za-z]/, "Must include at least one letter")
  .regex(/\d/, "Must include at least one number")
  .regex(/[^A-Za-z0-9]/, "Must include at least one special character");

export const phoneSchema = z
  .string()
  .min(1, "Phone number is required")
  .regex(/^\d{10}$/, "must have 10 digits");

export const nameSchema = z
  .string()
  .min(1, "Name is required")
  .min(1, "Minimum 1 character");

export const zipCodeSchema = z
  .string()
  .min(1, "Zip code is required")
  .regex(/^\d{5}(-\d{4})?$/, "Invalid zip code");

// Map generic field configuration to a Zod validator
export const createFieldValidator = (field) => {
  const {
    name,
    type,
    required,
    input_type,
    input_pattern,
    minLength,
    maxLength,
  } = field;

  let validator = z.string();
  if (required) {
    validator = validator.min(1, `${field.label || name} is required`);
  } else {
    validator = validator.optional();
  }

  switch (type) {
    case "numeric":
      // Numeric field (integer or currency) - can be top-level or nested
      const isInteger = field.inputType === "integer";
      const labelText = field.label || name;
      const toNumber = (v) =>
        v === "" || v === null || v === undefined ? undefined : Number(v);

      let numericValidator;

      if (required) {
        // Required field - build validator with min/max constraints
        let baseValidator = z.number({
          required_error: `${labelText} is required`,
          invalid_type_error: `${labelText} must be a number`,
        });

        // Apply min/max constraints
        if (field.min !== undefined) {
          baseValidator = baseValidator.min(
            field.min,
            `${labelText} must be at least ${field.min}`
          );
        } else if (isInteger) {
          // Default minimum for integer fields is 1
          baseValidator = baseValidator.min(
            1,
            `${labelText} must be at least 1`
          );
        }

        if (field.max !== undefined) {
          baseValidator = baseValidator.max(
            field.max,
            `${labelText} must be at most ${field.max}`
          );
        }

        numericValidator = z.preprocess(toNumber, baseValidator);
      } else {
        // Optional field - use refine for min/max constraints
        let baseValidator = z.number().optional();

        // Apply min/max constraints using refine
        if (field.min !== undefined) {
          baseValidator = baseValidator.refine(
            (val) => val === undefined || val >= field.min,
            {
              message: `${labelText} must be at least ${field.min}`,
            }
          );
        } else if (isInteger) {
          // Default minimum for integer fields is 1
          baseValidator = baseValidator.refine(
            (val) => val === undefined || val >= 1,
            {
              message: `${labelText} must be at least 1`,
            }
          );
        }

        if (field.max !== undefined) {
          baseValidator = baseValidator.refine(
            (val) => val === undefined || val <= field.max,
            {
              message: `${labelText} must be at most ${field.max}`,
            }
          );
        }

        numericValidator = z.preprocess(toNumber, baseValidator);
      }

      return numericValidator;

    case "input":
      switch (input_type) {
        case "email":
          return emailSchema;
        case "password":
          return passwordSchema;
        case "tel":
          return phoneSchema;
        case "date":
          return z.string().min(1, `${field.label || name} is required`);
        case "number": {
          const labelText = field.label || name;
          const toNumber = (v) =>
            v === "" || v === null || v === undefined ? undefined : Number(v);
          if (required) {
            return z.preprocess(
              toNumber,
              z
                .number({
                  required_error: `${labelText} is required`,
                  invalid_type_error: `${labelText} must be a number`,
                })
                .min(1, `${labelText} must be at least 1`)
            );
          }
          return z.preprocess(toNumber, z.number().optional());
        }
        case "text":
        default:
          if (name === "firstname" || name === "lastname") {
            return nameSchema;
          }
          if (name === "zipCode") {
            return zipCodeSchema;
          }
          break;
      }
      break;
    case "single_select":
    case "radio_group":
      return z
        .union([z.string(), z.number()])
        .refine((v) => v !== null && v !== undefined && String(v) !== "", {
          message: `Please select a ${field.label || name}`,
        });
    case "multi_select":
      // Special handling for incomeStates - should be an object keyed by year
      if (name === "incomeStates") {
        // Object where keys are years (strings) and values are arrays of states
        let incomeStatesValidator = z.record(
          z.string(),
          z.array(z.union([z.string(), z.number()]))
        );

        if (required) {
          // At least one year should have at least one state
          incomeStatesValidator = incomeStatesValidator.refine(
            (obj) => {
              if (!obj || typeof obj !== "object") return false;
              return Object.values(obj).some(
                (states) => Array.isArray(states) && states.length > 0
              );
            },
            {
              message: `${
                field.label || name
              } is required - please select at least one state for at least one year`,
            }
          );
        } else {
          incomeStatesValidator = incomeStatesValidator.optional();
        }

        return incomeStatesValidator;
      }

      // Default multi_select - Array of strings or numbers
      if (required) {
        return z
          .array(z.union([z.string(), z.number()]))
          .min(1, `Please select at least one ${field.label || name}`);
      } else {
        // Optional multi_select - allow empty array or undefined
        return z.array(z.union([z.string(), z.number()])).optional();
      }
    case "single_boolean_choice":
      // If field is required, the checkbox must be checked (value: true)
      // If field is not required, the field is optional (can be undefined, null, or { value: false })
      if (required) {
        return z.object({
          value: z.boolean().refine((val) => val === true, {
            message: `${field.label || name} is required`,
          }),
        });
      } else {
        // Optional field: accept undefined, null, or { value: boolean }
        // Use nullish() which handles both undefined and null, and make the object optional
        // This accepts: undefined, null, or { value: boolean } (where value can be true or false)
        return z.object({ value: z.boolean() }).nullish();
      }
    case "multi_boolean_choice": {
      // Build schema for nested fields
      const fieldsSchema = {};
      if (field.fields && Array.isArray(field.fields)) {
        field.fields.forEach((nestedField) => {
          if (nestedField.type === "numeric") {
            // Currency amount or integer - optional number with min/max support
            let numericValidator = z.number().optional();
            if (nestedField.min !== undefined) {
              numericValidator = numericValidator.refine(
                (val) => val === undefined || val >= nestedField.min,
                {
                  message: `${
                    nestedField.label || nestedField.name
                  } must be at least ${nestedField.min}`,
                }
              );
            }
            if (nestedField.max !== undefined) {
              numericValidator = numericValidator.refine(
                (val) => val === undefined || val <= nestedField.max,
                {
                  message: `${
                    nestedField.label || nestedField.name
                  } must be at most ${nestedField.max}`,
                }
              );
            }
            fieldsSchema[nestedField.name] = numericValidator;
          } else if (nestedField.type === "single_boolean_choice") {
            // Nested checkbox - object with value boolean
            fieldsSchema[nestedField.name] = z
              .object({ value: z.boolean() })
              .optional();
          } else if (nestedField.type === "multi_boolean_choice") {
            // Recursively handle nested multi_boolean_choice
            const nestedValidator = createFieldValidator(nestedField);
            fieldsSchema[nestedField.name] = nestedValidator.optional();
          } else if (nestedField.type === "radio_boolean_choice") {
            // Recursively handle nested radio_boolean_choice
            const nestedValidator = createFieldValidator(nestedField);
            fieldsSchema[nestedField.name] = nestedValidator.optional();
          } else if (nestedField.type === "multi_select") {
            // Nested multi_select - array of strings or numbers
            fieldsSchema[nestedField.name] = z
              .array(z.union([z.string(), z.number()]))
              .optional();
          }
        });
      }

      let baseSchema = z.object({
        value: z.boolean(),
        fields: z.object(fieldsSchema).optional(),
      });

      // Special validation for cryptoTransaction: if value is true, exchanges must have at least 1 selection
      if (name === "cryptoTransaction") {
        baseSchema = baseSchema.refine(
          (data) => {
            // If cryptoTransaction is false/not selected, validation passes
            if (!data.value) {
              return true;
            }
            // If cryptoTransaction is true, exchanges must be selected (at least 1)
            const exchanges = data?.fields?.exchanges;
            return Array.isArray(exchanges) && exchanges.length > 0;
          },
          {
            message: "Please select at least one exchange",
            path: ["fields", "exchanges"], // This will show the error on the exchanges field
          }
        );
      }

      return baseSchema;
    }
    case "radio_boolean_choice": {
      // Build schema for nested fields from all options
      const fieldsSchema = {};
      if (field.options && Array.isArray(field.options)) {
        field.options.forEach((option) => {
          if (option.fields && Array.isArray(option.fields)) {
            option.fields.forEach((nestedField) => {
              if (nestedField.type === "single_boolean_choice") {
                // Nested checkbox - object with value boolean
                fieldsSchema[nestedField.name] = z
                  .object({ value: z.boolean() })
                  .optional();
              }
            });
          }
        });
      }
      return z.object({
        value: z.string(), // Radio button value (e.g., "yes", "no")
        fields: z.object(fieldsSchema).optional(),
      });
    }
    case "composed_boolean_choice": {
      const { hide_input } = field || {};
      const min =
        typeof hide_input?.min === "number" ? hide_input.min : undefined;
      const max =
        typeof hide_input?.max === "number" ? hide_input.max : undefined;
      let valueSchema = z.number();
      if (typeof min === "number") valueSchema = valueSchema.min(min);
      if (typeof max === "number") valueSchema = valueSchema.max(max);
      return z.object({ status: z.boolean(), value: valueSchema });
    }
    case "single_address":
      // Validated via per-field rules and section validator; keep schema open
      return z.any();
    case "multi_address":
      // For multi_address, we need to validate that addresses cover all tax years
      // However, taxYears might not be available at schema creation time
      // So we use a refine that will check at validation time
      let addressValidator = z.array(z.any()).optional();

      if (required) {
        addressValidator = z
          .array(z.any())
          .min(1, "At least one address is required");
      }

      // Add custom validation for tax year coverage
      // This will be checked at runtime when trigger() is called
      return addressValidator.refine(
        (addresses) => {
          // This validation will be enhanced by the component's useEffect
          // which sets manual errors. The schema validation here is a fallback.
          // The component's validation takes precedence.
          return true; // Let component handle the detailed validation
        },
        {
          message: "Address coverage validation",
        }
      );
    // Handle individual address fields
    case "address_field":
      return required
        ? z.string().min(1, `${field.label || name} is required`)
        : z.string().optional();
    case "textarea":
      return required
        ? z.string().min(1, `${field.label || name} is required`)
        : z.string().optional();
    default:
      break;
  }

  // Temporary fallback for fields lacking explicit type in legacy schemas
  if (field?.name === "addresses") {
    return z.any();
  }

  if (input_pattern) {
    validator = validator.regex(
      new RegExp(input_pattern),
      `Invalid ${field.label || name} format`
    );
  }
  if (minLength) {
    validator = validator.min(minLength, `Minimum ${minLength} characters`);
  }
  if (maxLength) {
    validator = validator.max(maxLength, `Maximum ${maxLength} characters`);
  }

  return validator;
};

// Build Zod schemas from form configuration object
export const createFormSchema = (formSchema) => {
  const schemaFields = {};
  const sectionSchemas = {};

  Object.entries(formSchema).forEach(([sectionKey, sections]) => {
    const sectionFields = {};
    sections.forEach((section) => {
      section.fields.forEach((field) => {
        const fieldName = field.name;
        const validator = createFieldValidator(field);
        schemaFields[fieldName] = validator;
        sectionFields[fieldName] = validator;

        // Mirror CreateCPA behavior: when a section defines a single_address
        // field, also expose and validate its subfields individually so
        // react-hook-form can produce per-field errors.
        if (field.type === "single_address") {
          const addressSubfields = {
            address: z.string().min(1, "Street address is required"),
            city: z.string().min(1, "City is required"),
            stateAddress: z.string().min(1, "State is required"),
            zipCode: zipCodeSchema,
          };
          Object.entries(addressSubfields).forEach(
            ([subName, subValidator]) => {
              if (!schemaFields[subName]) schemaFields[subName] = subValidator;
              if (!sectionFields[subName])
                sectionFields[subName] = subValidator;
            }
          );
        }

        // Helper function to recursively register nested fields
        const registerNestedFields = (
          parentPath,
          nestedField,
          schemaFields,
          sectionFields
        ) => {
          const nestedFieldPath = `${parentPath}.${nestedField.name}`;
          let nestedValidator;

          if (nestedField.type === "numeric") {
            // Currency amount - optional number
            nestedValidator = z.number().optional();
          } else if (nestedField.type === "single_boolean_choice") {
            // Nested checkbox - object with value boolean
            nestedValidator = z.object({ value: z.boolean() }).optional();
          } else if (nestedField.type === "multi_boolean_choice") {
            // Recursively handle nested multi_boolean_choice
            nestedValidator = createFieldValidator(nestedField).optional();
            // Recursively register nested fields within this multi_boolean_choice
            if (nestedField.fields && Array.isArray(nestedField.fields)) {
              nestedField.fields.forEach((deepNestedField) => {
                registerNestedFields(
                  `${nestedFieldPath}.fields`,
                  deepNestedField,
                  schemaFields,
                  sectionFields
                );
              });
            }
          } else if (nestedField.type === "radio_boolean_choice") {
            // Recursively handle nested radio_boolean_choice
            nestedValidator = createFieldValidator(nestedField).optional();
            // Recursively register nested fields within radio options
            if (nestedField.options && Array.isArray(nestedField.options)) {
              nestedField.options.forEach((option) => {
                if (option.fields && Array.isArray(option.fields)) {
                  option.fields.forEach((deepNestedField) => {
                    registerNestedFields(
                      `${nestedFieldPath}.fields`,
                      deepNestedField,
                      schemaFields,
                      sectionFields
                    );
                  });
                }
              });
            }
          } else if (nestedField.type === "multi_select") {
            // Nested multi_select - array of strings or numbers
            nestedValidator = z
              .array(z.union([z.string(), z.number()]))
              .optional();
          }

          if (nestedValidator) {
            if (!schemaFields[nestedFieldPath]) {
              schemaFields[nestedFieldPath] = nestedValidator;
            }
            if (!sectionFields[nestedFieldPath]) {
              sectionFields[nestedFieldPath] = nestedValidator;
            }
          }
        };

        // Register nested fields for multi_boolean_choice so react-hook-form can validate them
        if (
          field.type === "multi_boolean_choice" &&
          field.fields &&
          Array.isArray(field.fields)
        ) {
          field.fields.forEach((nestedField) => {
            registerNestedFields(
              `${fieldName}.fields`,
              nestedField,
              schemaFields,
              sectionFields
            );
          });
        }

        // Special handling for incomeStates - register nested fields for each potential year
        // Note: We can't know all years upfront, but react-hook-form will handle dynamic paths
        // The main validation is on the parent incomeStates object

        // Register nested fields for radio_boolean_choice so react-hook-form can validate them
        if (
          field.type === "radio_boolean_choice" &&
          field.options &&
          Array.isArray(field.options)
        ) {
          field.options.forEach((option) => {
            if (option.fields && Array.isArray(option.fields)) {
              option.fields.forEach((nestedField) => {
                const nestedFieldName = `${fieldName}.${nestedField.name}`;
                let nestedValidator;

                if (nestedField.type === "single_boolean_choice") {
                  // Nested checkbox - object with value boolean
                  nestedValidator = z.object({ value: z.boolean() }).optional();
                }

                if (nestedValidator) {
                  if (!schemaFields[nestedFieldName]) {
                    schemaFields[nestedFieldName] = nestedValidator;
                  }
                  if (!sectionFields[nestedFieldName]) {
                    sectionFields[nestedFieldName] = nestedValidator;
                  }
                }
              });
            }
          });
        }
      });
    });
    sectionSchemas[sectionKey] = z.object(sectionFields);
  });

  let completeSchema = z.object(schemaFields);

  // Optional common refinements
  if (schemaFields.termsConditions) {
    completeSchema = completeSchema.refine(
      (data) => data.termsConditions?.value === true,
      {
        message: "You must accept the terms and conditions",
        path: ["termsConditions"],
      }
    );
  }

  if (schemaFields.userAgreement) {
    completeSchema = completeSchema.refine(
      (data) => data.userAgreement?.value === true,
      {
        message: "You must accept the user agreement",
        path: ["userAgreement"],
      }
    );
  }

  const getFieldsForSection = (sectionKey) => {
    const section = formSchema[sectionKey];
    if (!section) return [];
    const fields = [];
    section.forEach((s) => s.fields.forEach((f) => fields.push(f.name)));
    return fields;
  };

  return { completeSchema, sectionSchemas, getFieldsForSection };
};
